import { Component, signal, inject, input } from '@angular/core';
import { RecommendationDto, RecommendationStatus } from '@shared/models';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ButtonV3Component, IconV3Component, SnackbarModule } from '@gea/digital-ui-lib';
import { BRCBaseComponent } from '@brc/hrt-base-component';
import { TroubleshootingService } from '@shared/services';
import { finalize } from 'rxjs/operators';

import { ChangeDetectionStrategy } from '@angular/core';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gea-hrt-readonly-actions',
  standalone: true,
  styleUrl: 'actions.component.scss',
  imports: [TranslatePipe, ButtonV3Component, IconV3Component, SnackbarModule],
  template: `
    <div class="readonly-actions">
      @switch (recommendation().status) {
        @case (Status.SKIPPED) {
          <span class="label variant-skipped" data-testid="skipped-label">
            {{ 'TROUBLESHOOTING.RECOMMENDATIONS.LABEL_SKIPPED' | translate }}
          </span>
        }
        @case (Status.DONE) {
          <span class="label variant-done" data-testid="done-label">
            {{ 'TROUBLESHOOTING.RECOMMENDATIONS.LABEL_DONE' | translate }}
          </span>
        }
        @case (Status.PENDING) {
          <geaui-button-v3
            type="tertiary"
            data-testid="btn-skip-task"
            [loading]="loading()"
            (click)="updateStatus(Status.SKIPPED)">
            {{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_SKIP_TASK' | translate }}
          </geaui-button-v3>
        }
      }

      @if (recommendation().status === Status.PENDING) {
        <geaui-button-v3 type="tertiary" data-testid="btn-done" [loading]="loading()" (click)="updateStatus(Status.DONE)">
          {{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_DONE' | translate }}
        </geaui-button-v3>
      } @else {
        <geaui-icon-v3
          class="reset"
          title="{{ 'TROUBLESHOOTING.RECOMMENDATIONS.BUTTON_RESET' | translate }}"
          file="reload"
          data-testid="btn-reset"
          (click)="updateStatus(Status.PENDING)" />
      }
    </div>

    <geaui-snackbar />
  `,
  styles: `
    button.p-button.p-button-rounded {
      border: none !important;
    }
  `,
})
export class ReadonlyActionsComponent extends BRCBaseComponent {
  readonly Status = RecommendationStatus;
  loading = signal(false);

  readonly recommendation = input.required<RecommendationDto>();

  readonly translate = inject(TranslateService);
  private readonly troubleshootingService = inject(TroubleshootingService);

  updateStatus(status: RecommendationStatus): void {
    this.loading.set(true);

    this.troubleshootingService
      .setRecommendationStatus({ recommendationId: this.recommendation().id, recommendationStatus: status })
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.loading.set(false);
          }, 1000);
        })
      )
      .subscribe({
        next: () => {
          this.showSuccess('SUCCESS.UPDATE_RECOMMENDATION_SUCCESS');
        },
        error: () => {
          this.showError('ERROR.UPDATE_RECOMMENDATION_FAILED');
        },
      });
  }
}
